import PageLayout from '@/components/Layouts/PageLayout';
import useScreen from '@/hooks/useScreen';
import Container from '@/shared/Container';
import Div from '@/shared/Div';
import Heading from '@/shared/Heading';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { getEnvValue } from '@/utils/envUtils';
import React, { useEffect } from 'react';
import Divider from '@/shared/Divider';
import { NextPageContext } from 'next';
import { getStaticContent } from '@/actions/ecom/staticContent';
import { fetchServiceCentres } from '@/actions/fetchLocations';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import PhoneIcon from '@/components/Icons/PhoneIcon';
import Image from '@/shared/Image';
import whatsappImage from '../../../public/whatsappImage.png';
import playStoreLogo from '../../../public/googleplay.png';
import appStoreLogo from '../../../public/appstore.png';
import Link from '@/shared/Link';
import Script from 'next/script';
const API_KEY = getEnvValue('GOOGLE_MAP_API_KEY');

interface LocationProps {
  name: string;
  serviceCenters: any;
  pincode: string;
  address: string;
  type: string;
  serviceEmail: string | null;
  servicePhone: string;
  saleEmail: string | null;
  salePhone: string;
  latitude: number;
  longitude: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  sitemap_exclude: boolean;

  city: {
    data: {
      id: number;
      attributes: {
        cityId: string;
        name: string;
        createdAt: string;
        updatedAt: string;
        publishedAt: string;
        sitemap_exclude: boolean;
      };
    };
  };
}

function Location({ serviceCenters }: LocationProps) {
  const { isMobile, isTablet } = useScreen();
  const isMobileOrTablet = isMobile || isTablet;
  const router = useRouter();
  const { slug } = router.query;
  const {
    name,
    pincode,
    address,
    servicePhone,
    salePhone,
    latitude: lat,
    longitude: lng,
    seo,
  } = serviceCenters || {};
  const cityName = serviceCenters?.city?.data?.attributes?.name;

  const latLong = lat && lng ? { lat, lng } : null;

  const renderMap = () => (
    <Div
      className={classNames('relative rounded-cs overflow-hidden', {
        'w-[650px] h-[500px]': !isMobileOrTablet,
        'w-full h-[300px]': isMobileOrTablet,
      })}
    >
      {latLong && (
        <APIProvider apiKey={API_KEY}>
          <Map
            defaultZoom={14}
            defaultCenter={latLong}
            center={latLong}
            gestureHandling={'none'}
            streetViewControl={false}
            zoomControl={false}
            fullscreenControl={false}
            mapTypeControl={false}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
            }}
          >
            <Div
              className="absolute"
              style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
            >
              <Marker position={latLong} />
            </Div>
          </Map>
        </APIProvider>
      )}
    </Div>
  );

  return (
    <PageLayout seo={seo}>
      <Div className="relative bg-cs-secondary min-h-screen">
        <Container className="pt-s5 pb-s5">
          <Div className="mb-s3">
            <Heading level={2} className="text-h-2">
              Eureka Forbes Authorized Service Center:
            </Heading>
          </Div>
          <Div className="text-body-lg text-cti-secondary">
            Aquaguard Water Purifiers, Forbes Vacuum Cleaners, Aquaguard Water Softeners & Forbes
            Air Purifiers
          </Div>

          <Div
            className={classNames('flex items-start gap-s8 mt-s7', {
              'w-[1240px] flex-row': !isMobileOrTablet,
              'w-full flex-col': isMobileOrTablet,
            })}
          >
            {/*  */}
            <Div className="flex flex-col items-start gap-s7 flex-1">
              <Div className="flex flex-col items-start gap-s3 w-full rounded-cxs bg-cs-primary p-s5">
                <Heading level={3} className="text-h-3">
                  {name}
                </Heading>

                <Divider className="w-full my-s4" />
                <Div className="flex flex-col items-start gap-s1 w-full">
                  <Div className="text-body-lg text-cti-secondary">Address:</Div>
                  <Div className="text-body-lg">{address}</Div>
                </Div>
                <Div
                  className={classNames({
                    'flex flex-row items-start gap-s8 w-full mt-s1': !isMobileOrTablet,
                    'flex flex-col items-start gap-s1 w-full': isMobileOrTablet,
                  })}
                >
                  <Div
                    className={classNames({
                      'flex flex-col items-start': isMobileOrTablet,
                      'flex flex-col items-start flex-1': !isMobileOrTablet,
                    })}
                  >
                    <Div className="text-body-lg text-cti-secondary">City:</Div>
                    <Div className="text-body-lg">{cityName}</Div>
                  </Div>
                  <Div
                    className={classNames({
                      'flex flex-col items-start': isMobileOrTablet,
                      'flex flex-col items-start flex-1': !isMobileOrTablet,
                    })}
                  >
                    <Div className="text-body-lg text-cti-secondary">Country:</Div>
                    <Div className="text-body-lg">India</Div>
                  </Div>
                  <Div
                    className={classNames({
                      'flex flex-col items-start': isMobileOrTablet,
                      'flex flex-col items-start flex-1': !isMobileOrTablet,
                    })}
                  >
                    <Div className="text-body-lg text-cti-secondary">Zip:</Div>
                    <Div className="text-body-lg">{pincode}</Div>
                  </Div>
                </Div>
              </Div>
              <Div className="flex flex-col items-start gap-s4 p-4 w-full rounded-cxs bg-cs-primary">
                <Heading level={2} className="text-h-2">
                  Download the Eureka Forbes App & Book Aquaguard Genuine Service in Seconds!
                </Heading>

                <Div className="flex items-start gap-s3 ">
                  <Link
                    href="https://apps.apple.com/in/app/eureka-forbes-aquaguard/id1463742085"
                    className="flex-1"
                    target="_blank"
                  >
                    <Image
                      src={appStoreLogo.src}
                      alt="App Store"
                      className="w-full object-contain"
                    />
                  </Link>
                  <Link
                    href="https://play.google.com/store/apps/details?id=com.efl.eurekaforbes&hl=en_IN"
                    className="flex-1"
                    target="_blank"
                  >
                    <Image
                      src={playStoreLogo.src}
                      alt="Play Store"
                      className="w-full object-contain"
                    />
                  </Link>
                </Div>
              </Div>
              <Div className="flex flex-col items-start gap-s5 w-full rounded-cxs bg-cs-primary p-s6">
                <Heading level={3} className="text-h-3">
                  Log Service Requests:
                </Heading>

                <Div
                  className={classNames({
                    'flex flex-row items-center gap-s4 w-full': !isMobileOrTablet,
                    'flex flex-col items-start gap-s5 w-full': isMobileOrTablet,
                  })}
                >
                  <Div className="flex items-center gap-s4">
                    <Image src={whatsappImage.src} alt="WhatsappButton" />
                    <Link
                      href={`https://wa.me/${servicePhone}`}
                      className="text-body-lg text-cu-selected"
                    >
                      {servicePhone}
                    </Link>
                  </Div>
                  <Div className="flex items-center gap-s4">
                    <PhoneIcon />
                    <Link href={`tel:${servicePhone}`} className="text-body-lg text-cu-selected">
                      {servicePhone}
                    </Link>
                  </Div>
                </Div>
              </Div>
            </Div>
            {renderMap()}
          </Div>
        </Container>
      </Div>
      <Script
        id="local-business-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            name: `Eureka Forbes Authorized Service Center - ${cityName} ${pincode}`,
            description: `Official Eureka Forbes Authorized Service Center in ${cityName} (${pincode}) offering expert repairs, maintenance, and genuine spare parts for Aquaguard Water Purifiers, Forbes Vacuum Cleaners, Aquaguard Water Softeners, and Forbes Air Purifiers. Our certified technicians provide same-day service, warranty support, and doorstep assistance across ${cityName}. Trusted by customers for authentic Eureka Forbes products.`,
            image: 'https://www.eurekaforbes.com/images/favicon.ico',
            url: `https://www.eurekaforbes.com/authorized-center/${slug?.[0]}/${slug?.[1]}`,
            telephone: servicePhone || '+91-7039883333',
            address: {
              '@type': 'PostalAddress',
              streetAddress: address,
              addressLocality: cityName,
              postalCode: pincode,
              addressRegion: cityName,
              addressCountry: 'IN',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: lat,
              longitude: lng,
            },
            openingHoursSpecification: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
                'Sunday',
              ],
              opens: '09:00',
              closes: '18:00',
            },
            serviceArea: {
              '@type': 'AdministrativeArea',
              name: cityName,
            },
            sameAs: [
              'https://www.facebook.com/EurekaForbes/',
              'https://x.com/eurekaforbes',
              'https://www.linkedin.com/company/eureka-forbes-ltd/',
              'https://www.instagram.com/eurekaforbesofficial/',
            ],
          }),
        }}
      />
    </PageLayout>
  );
}

export const getServerSideProps = async (context: NextPageContext) => {
  try {
    const { query } = context;
    const type = 'authorized-center';
    const promises = [
      getStaticContent('/store-locator'),
      await fetchServiceCentres(type, query?.slug?.[1] as string),
    ];
    const [page, serviceCentersResponse] = await Promise.all(promises);
    return {
      props: {
        page,
        type,
        serviceCenters: serviceCentersResponse,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, context);
  }
};

export default Location;
